import { type JSX, useCallback } from 'react';
import { useMediaQuery } from 'react-responsive';

import { PresentIcon } from '@cx-modules/ui';

import { useBookmakersAll } from '../../hooks/useBookmakersAll';
import { useOffersTooltipPosition } from '../../hooks/useOffersTooltipPosition';
import { OffersList } from '../OffersList';
import { BookmakerSeparator } from '../shared-styles';

import { OffersPortalWithScroll } from './OffersPortalScrollContainer';
import {
  OfferDesktopCellBottom,
  OfferDesktopCellText,
  OfferDesktopCellTop,
  OfferDesktopContainer,
  offerItemWidth,
  roundedCell,
  TooltipWrapper,
} from './style';

export const OffersGridCellDesktop = ({ bookieCode }: { bookieCode: string }): JSX.Element | null => {
  const bookmaker = useBookmakersAll().find(bookmaker => bookmaker.code === bookieCode);

  const { offers } = bookmaker || {};
  const { signupOffers = [], specialOffers = [] } = offers || {};

  const hasSignupOffers = signupOffers.length > 0;
  const hasSpecialOffers = specialOffers.length > 0;

  const signupOffersWidth = signupOffers.length * (offerItemWidth + 1);
  const specialOffersWidth = specialOffers.length * (offerItemWidth + 1);

  const {
    coordinates: coordinatesSU,
    onCardMouseEnter: onCardMouseEnterSU,
    onCardMouseLeave: onCardMouseLeaveSU,
    onMouseEnter: onMouseEnterSU,
    onMouseLeave: onMouseLeaveSU,
    show: showSU,
  } = useOffersTooltipPosition(signupOffersWidth);
  const {
    coordinates: coordinatesSP,
    onCardMouseEnter: onCardMouseEnterSP,
    onCardMouseLeave: onCardMouseLeaveSP,
    onMouseEnter: onMouseEnterSP,
    onMouseLeave: onMouseLeaveSP,
    show: showSP,
  } = useOffersTooltipPosition(specialOffersWidth);

  const isTablet = useMediaQuery({
    query: `(min-width: 768px) and (max-width: 980px)`,
  });

  const signUpOffersIsScrollableCallback = useCallback(() => {
    return isTablet && signupOffersWidth > window?.innerWidth;
  }, [isTablet, signupOffersWidth]);

  const specialOffersIsScrollableCallback = useCallback(() => {
    return isTablet && specialOffersWidth > window?.innerWidth;
  }, [isTablet, specialOffersWidth]);

  if (!bookmaker) return null;

  return (
    <OfferDesktopContainer data-bk={bookieCode} data-testid="signup-offer">
      {hasSignupOffers && (
        <>
          <OfferDesktopCellTop
            className={hasSpecialOffers ? '' : roundedCell}
            onMouseEnter={onMouseEnterSU}
            onMouseLeave={onMouseLeaveSU}
            role="tooltip"
          >
            <OfferDesktopCellText>{signupOffers[0].amount}</OfferDesktopCellText>
          </OfferDesktopCellTop>
          {showSU && (
            <OffersPortalWithScroll coordinates={coordinatesSU} isScrollableCallback={signUpOffersIsScrollableCallback}>
              <TooltipWrapper
                onMouseEnter={onCardMouseEnterSU}
                onMouseLeave={onCardMouseLeaveSU}
                width={signupOffersWidth}
              >
                <OffersList bookmaker={bookmaker} eventLabel="bookie_offer_bar_su" offers={signupOffers} />
              </TooltipWrapper>
            </OffersPortalWithScroll>
          )}
        </>
      )}
      <BookmakerSeparator />
      {hasSpecialOffers && (
        <>
          <OfferDesktopCellBottom onMouseEnter={onMouseEnterSP} onMouseLeave={onMouseLeaveSP}>
            <PresentIcon />

            <OfferDesktopCellText>{specialOffers.length}</OfferDesktopCellText>
          </OfferDesktopCellBottom>
          {showSP && (
            <OffersPortalWithScroll
              coordinates={coordinatesSP}
              isScrollableCallback={specialOffersIsScrollableCallback}
            >
              <TooltipWrapper
                onMouseEnter={onCardMouseEnterSP}
                onMouseLeave={onCardMouseLeaveSP}
                width={specialOffersWidth}
              >
                <OffersList bookmaker={bookmaker} eventLabel="bookie_offer_bar_sp" offers={specialOffers} />
              </TooltipWrapper>
            </OffersPortalWithScroll>
          )}
        </>
      )}
    </OfferDesktopContainer>
  );
};
